/**
 * permissionDiff
 *
 * Classifies how a user's EFFECTIVE permission set changed between two
 * ``User`` snapshots (the in-memory one held by AuthContext vs a fresh
 * ``/auth/me/`` payload fetched after a ``permissions_changed`` realtime
 * event).
 *
 * The product rule (see PermissionChangeGate):
 *   - ANY effective grant removed  → 'downgrade'  (forced refresh, even
 *                                     if other grants were added at the
 *                                     same time — a downgrade always wins)
 *   - only additions               → 'upgrade'    (dismissible)
 *   - identical effective set      → 'none'       (e.g. a role rename or
 *                                     reorder — no modal)
 *
 * Correctness note — why we evaluate through ``hasPermission``:
 * the permission tree is scoped (company / per-location) and carries
 * three ``scopeStrategy`` variants plus the ``company.full_access``
 * master key. Rather than re-encode that logic here, we flatten each
 * snapshot into the set of (codename × scope) grants that
 * ``hasPermission`` — the exact predicate every UI gate uses — answers
 * true for, over a shared codename/location universe. This makes the
 * full_access shortcut fall out for free: when a snapshot has
 * full_access, ``hasPermission`` returns true for every codename, so
 * that snapshot's key set dominates and no spurious "removed" is
 * reported (gaining full_access reads as an upgrade; losing it as a
 * downgrade).
 */

import type { User } from './types';
import { hasPermission, getAccessibleLocationIds } from './permissions';

export type PermissionChangeDirection = 'upgrade' | 'downgrade' | 'none';

function collectCodenames(user: User | null | undefined, into: Set<string>): void {
  if (!user) return;
  for (const ar of user.accessRoles) {
    for (const rp of ar.rolePermissions) {
      into.add(rp.permissionDetail?.codename ?? rp.permission);
    }
  }
}

/**
 * Flatten a user's effective grants into a set of scoped keys, evaluated
 * with the same ``hasPermission`` predicate the UI gates on.
 *
 * Keys:
 *   - ``company::{codename}``     — granted at company (null-location) scope
 *   - ``loc:{locationId}::{codename}`` — granted at that specific location
 *
 * ``codenameUniverse`` / ``locationUniverse`` must be the UNION across
 * both snapshots being compared, so the two resulting sets are directly
 * comparable.
 */
export function flattenEffectivePermissions(
  user: User | null | undefined,
  codenameUniverse: string[],
  locationUniverse: string[],
): Set<string> {
  const keys = new Set<string>();
  if (!user) return keys;

  for (const codename of codenameUniverse) {
    if (hasPermission(user, codename, null)) {
      keys.add(`company::${codename}`);
    }
    for (const locationId of locationUniverse) {
      if (hasPermission(user, codename, locationId)) {
        keys.add(`loc:${locationId}::${codename}`);
      }
    }
  }

  return keys;
}

/**
 * Classify the change between ``oldUser`` (session's current in-memory
 * user) and ``newUser`` (freshly fetched from /auth/me/).
 */
export function classifyPermissionChange(
  oldUser: User | null | undefined,
  newUser: User | null | undefined,
): PermissionChangeDirection {
  const codenameUniverse = new Set<string>();
  collectCodenames(oldUser, codenameUniverse);
  collectCodenames(newUser, codenameUniverse);

  const locationUniverse = new Set<string>([
    ...getAccessibleLocationIds(oldUser),
    ...getAccessibleLocationIds(newUser),
  ]);

  const codenames = Array.from(codenameUniverse);
  const locations = Array.from(locationUniverse);

  const oldKeys = flattenEffectivePermissions(oldUser, codenames, locations);
  const newKeys = flattenEffectivePermissions(newUser, codenames, locations);

  // A removal always wins — even if additions happened in the same
  // change — because the session may still expose actions the user can
  // no longer perform until they refresh.
  for (const key of oldKeys) {
    if (!newKeys.has(key)) return 'downgrade';
  }

  for (const key of newKeys) {
    if (!oldKeys.has(key)) return 'upgrade';
  }

  return 'none';
}
